import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Container from '../../../../components/Container';
import Loader from '../../../../components/Loader';
import AlertMsg from '../../../../components/AlertMsg';
import AppDialog from '../../../../components/AppDialog';
import RestaurantHeader from '../../../../components/RestaurantHeader';

import { RestaurantConfigsList, RestaurantConfigDialog } from '../components';
import {
  useGetPaymentProviders,
  useGetRestaurantPaymentConfigs,
  useUpsertRestaurantConfig,
  useDeleteRestaurantConfig,
  useSetDefaultRestaurantConfig,
} from '../../../PaymentProviders/services';
import { TRestaurantPaymentConfig } from '../../../PaymentProviders/schemas/payment-provider-schema';
import { useGetRestaurantData, useUpdateRestaurantData } from '../../../../api/useQueryHooks';

type KioskSettingsForm = {
  isKioskEnabled: boolean;
  businessDayBoundaryHour: string;
};

const getInitialForm = (restaurantData: any): KioskSettingsForm => ({
  isKioskEnabled: restaurantData?.kioskOptions?.isEnabled ?? false,
  businessDayBoundaryHour:
    restaurantData?.kioskOptions?.businessDayBoundaryHour != null
      ? String(restaurantData.kioskOptions.businessDayBoundaryHour)
      : '',
});

const RestaurantPaymentProvidersPage = () => {
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const navigate = useNavigate();

  const { data: restaurantData } = useGetRestaurantData(restaurantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingProviderId, setEditingProviderId] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<TRestaurantPaymentConfig | null>(null);

  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  const [settingsForm, setSettingsForm] = useState<KioskSettingsForm>(() =>
    getInitialForm(restaurantData),
  );
  const [savedForm, setSavedForm] = useState<KioskSettingsForm>(() =>
    getInitialForm(restaurantData),
  );

  // Re-sync when restaurantData loads (e.g. after navigation)
  useEffect(() => {
    const initial = getInitialForm(restaurantData);
    setSettingsForm(initial);
    setSavedForm(initial);
  }, [restaurantData?._id]);

  const isDirty =
    settingsForm.isKioskEnabled !== savedForm.isKioskEnabled ||
    settingsForm.businessDayBoundaryHour !== savedForm.businessDayBoundaryHour;

  const hourVal = settingsForm.businessDayBoundaryHour;
  const hourError = (() => {
    if (hourVal === '') return null;
    const n = Number(hourVal);
    if (!Number.isInteger(n) || n < 0 || n > 23) return 'Must be between 0 and 23';
    return null;
  })();

  const { data: providersData = [] } = useGetPaymentProviders();
  const { data: configsData, isLoading } = useGetRestaurantPaymentConfigs(restaurantId || '');

  const upsertMutation = useUpsertRestaurantConfig(restaurantId || '');
  const deleteMutation = useDeleteRestaurantConfig(restaurantId || '');
  const defaultMutation = useSetDefaultRestaurantConfig(restaurantId || '');

  const updateMutation = useUpdateRestaurantData(
    () => {
      setSavedForm(settingsForm);
      setMsgAlert({ open: true, message: 'Settings saved', msgType: 'success' });
    },
    () => {
      setMsgAlert({ open: true, message: 'Failed to save settings', msgType: 'error' });
    },
  );

  const configs = useMemo(() => configsData?.rows || [], [configsData]);

  const availableProvidersForCreate = useMemo(() => {
    const existingIds = new Set(configs.map((c) => c.providerId));
    return providersData
      .filter((p) => p.isEnabled)
      .filter((p) => !existingIds.has(p.providerId));
  }, [providersData, configs]);

  const editingConfig = useMemo(
    () =>
      dialogMode === 'edit' && editingProviderId
        ? configs.find((c) => c.providerId === editingProviderId) || null
        : null,
    [dialogMode, editingProviderId, configs],
  );

  const handleAdd = () => {
    setEditingProviderId(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleEdit = (config: TRestaurantPaymentConfig) => {
    setEditingProviderId(config.providerId);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProviderId(null);
  };

  // The dialog validates required fields and drops unchanged secrets before
  // handing the payload over, so this just persists it.
  const handleSave = async (providerId: string, clientFields: Record<string, string>) => {
    try {
      await upsertMutation.mutateAsync({ providerId, payload: { clientFields } });
      setMsgAlert({
        open: true,
        message: dialogMode === 'edit' ? 'Config updated' : 'Config added',
        msgType: 'success',
      });
      setDialogOpen(false);
      setEditingProviderId(null);
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to save config',
        msgType: 'error',
      });
    }
  };

  const handleDeleteClick = (config: TRestaurantPaymentConfig) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;
    try {
      await deleteMutation.mutateAsync(configToDelete.providerId);
      setMsgAlert({ open: true, message: 'Config deleted', msgType: 'success' });
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to delete config',
        msgType: 'error',
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  const handleSetDefault = async (config: TRestaurantPaymentConfig) => {
    setSettingDefaultId(config.providerId);
    try {
      await defaultMutation.mutateAsync(config.providerId);
      setMsgAlert({
        open: true,
        message: `${config.displayName || config.providerId} set as default`,
        msgType: 'success',
      });
    } catch (err: any) {
      setMsgAlert({
        open: true,
        message: err?.response?.data?.msg || 'Failed to set default',
        msgType: 'error',
      });
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleSaveSettings = () => {
    const kioskOptions: Record<string, unknown> = {
      isEnabled: settingsForm.isKioskEnabled,
    };
    // Only send the boundary hour when kiosk mode is on and a value was entered —
    // otherwise a toggle-off would persist whatever stale hour was last loaded.
    if (settingsForm.isKioskEnabled && hourVal !== '') {
      kioskOptions.businessDayBoundaryHour = Number(hourVal);
    }

    updateMutation.mutate({ _id: restaurantId, kioskOptions } as any);
  };

  const timezone = restaurantData?.timeZone || '—';
  const minCollectionAmount = restaurantData?.minimumCollectionOrderAmount;

  // The restaurant update endpoint is a pass-through PUT, so an unsupported or
  // renamed field would be dropped silently and the toggle would never stick.
  const kioskOptionsMissing = !!restaurantData && restaurantData.kioskOptions === undefined;

  return (
    <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <RestaurantHeader
            title="Payment Providers"
            onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
          />
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={2} pb={3}>
          {isLoading ? (
            <Box sx={styles.loaderContainer}>
              <Loader />
            </Box>
          ) : (
            <>
              <Typography sx={styles.sectionTitle} mb={2}>Kiosk Settings</Typography>

              <Box sx={styles.settingsCard}>
                {/* Row 1 — Timezone */}
                <Box sx={styles.settingRow}>
                  <Typography sx={styles.settingLabel}>Timezone</Typography>
                  <Typography sx={styles.settingValue}>{timezone}</Typography>
                </Box>

                <Divider />

                {/* Row 2 — Min Collection Order Amount */}
                <Box sx={styles.settingRow}>
                  <Typography sx={styles.settingLabel}>Min Collection Order Amount</Typography>
                  <Box sx={styles.settingValue}>
                    <Typography component="span" sx={styles.settingValue}>
                      {minCollectionAmount != null ? minCollectionAmount : '—'}
                    </Typography>
                    <Tooltip title="Edit in Takeaway Settings">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/restaurant/${restaurantId}/takeaway_settings`, { state: { highlight: 'minimumCollectionOrderAmount' } })}
                        sx={{ ml: 0.5, color: '#6b7368' }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Divider />

                {/* Row 3 — Kiosk Mode toggle */}
                <Box sx={styles.settingRow}>
                  <Typography sx={styles.settingLabel}>
                    {settingsForm.isKioskEnabled ? 'Kiosk Mode Enabled' : 'Kiosk Mode Disabled'}
                  </Typography>
                  <Switch
                    checked={settingsForm.isKioskEnabled}
                    onChange={(e) =>
                      setSettingsForm((f) => ({ ...f, isKioskEnabled: e.target.checked }))
                    }
                    sx={styles.switch}
                  />
                </Box>

                {/* Row 4 — Business Day Boundary Hour (visible only when kiosk enabled) */}
                {settingsForm.isKioskEnabled && (
                  <>
                    <Divider />
                    <Box sx={styles.settingRow}>
                      <Box sx={styles.labelWithInfo}>
                        <Typography sx={styles.settingLabel}>Business Day Boundary Hour</Typography>
                        <Tooltip
                          title="Hour in venue's local time when the daily collection-number counter resets (e.g. 4 = 04:00). Set this after the venue's typical closing time."
                          placement="top"
                          arrow
                        >
                          <InfoOutlinedIcon sx={styles.infoIcon} />
                        </Tooltip>
                      </Box>
                      <TextField
                        value={settingsForm.businessDayBoundaryHour}
                        onChange={(e) =>
                          setSettingsForm((f) => ({ ...f, businessDayBoundaryHour: e.target.value }))
                        }
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 23, step: 1 }}
                        placeholder="0 – 23"
                        error={!!hourError}
                        sx={styles.hourInput}
                      />
                    </Box>
                    {hourError && (
                      <Box sx={styles.errorRow}>
                        <Typography sx={styles.errorText}>{hourError}</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <Box mb={3}>
                <Button
                  sx={styles.saveBtn}
                  onClick={handleSaveSettings}
                  disabled={!isDirty || !!hourError || updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save Settings'}
                </Button>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={styles.headerRow} mb={2}>
                <Typography sx={styles.sectionTitle}>Configured Providers</Typography>
                <Button
                  sx={styles.addBtn}
                  onClick={handleAdd}
                  disabled={availableProvidersForCreate.length === 0}
                >
                  + Add Provider
                </Button>
              </Box>

              {!savedForm.isKioskEnabled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Kiosk mode is off for this venue. Credentials saved here are still stored and
                  read by POS devices — turn kiosk mode on above when the venue gets a kiosk.
                </Alert>
              )}

              {kioskOptionsMissing && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This restaurant record has no <code>kioskOptions</code>. The Kiosk Settings above
                  may not persist — verify the field is supported on the restaurant update
                  endpoint before relying on the toggle.
                </Alert>
              )}

              <RestaurantConfigsList
                configs={configs}
                providers={providersData}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onSetDefault={handleSetDefault}
                settingDefaultId={settingDefaultId}
              />
            </>
          )}
        </Grid>
      </Grid>

      <RestaurantConfigDialog
        open={dialogOpen}
        mode={dialogMode}
        providerId={editingProviderId}
        existingConfig={editingConfig}
        availableProviders={availableProvidersForCreate}
        onClose={handleCloseDialog}
        onSave={handleSave}
        isSaving={upsertMutation.isPending}
      />

      <AppDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title="Delete Provider Config?"
        content={`This will remove ${configToDelete?.displayName || configToDelete?.providerId} from this restaurant. Kiosks will no longer see it.`}
        firstBtnContent="Cancel"
        secondBtnContent="Delete"
        firstBtnClickHandler={handleDeleteCancel}
        secondBtnClickHandler={handleDeleteConfirm}
        options={{ keepMounted: false, btnLoading: deleteMutation.isPending }}
      />

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default RestaurantPaymentProvidersPage;

const styles = {
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  loaderContainer: {
    width: '100%',
    minHeight: 'calc(100vh - 300px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    fontFamily: 'GilroyBold',
    color: '#000',
  },
  settingsCard: {
    border: '1px solid #E5EAD9',
    borderRadius: '8px',
    mb: 2.5,
    overflow: 'hidden',
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2.5,
    py: 1.75,
    minHeight: 56,
  },
  settingLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
    color: '#1a1a1a',
  },
  settingValue: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#4a4a4a',
    display: 'flex',
    alignItems: 'center',
  },
  labelWithInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  infoIcon: {
    fontSize: 16,
    color: '#9aa39a',
    cursor: 'default',
  },
  hourInput: {
    width: 120,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontFamily: 'GilroyRegular',
      fontSize: 14,
    },
  },
  errorRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    px: 2.5,
    pb: 1.25,
  },
  errorText: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: '#d32f2f',
  },
  switch: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#00c853',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#00c853',
    },
  },
  saveBtn: {
    backgroundColor: '#053418',
    color: '#FFF',
    fontSize: 14,
    borderRadius: '8px',
    textTransform: 'capitalize' as const,
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    fontFamily: 'GilroySemiBold',
    '&:hover': {
      backgroundColor: '#0a5c2a',
    },
    '&:disabled': {
      opacity: 0.5,
      color: '#FFF',
    },
  },
  addBtn: {
    backgroundColor: '#000',
    color: '#FFF',
    fontSize: 14,
    borderRadius: '8px',
    textTransform: 'capitalize' as const,
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    fontFamily: 'GilroySemiBold',
    '&:hover': {
      backgroundColor: '#333',
    },
    '&:disabled': {
      opacity: 0.5,
      color: '#FFF',
    },
  },
};
